/**
 * Dosage autofill (#692): what a drug picked in step 1 fills in the dosage
 * step — the drug's own strength and form at once, then how the clinic last
 * prescribed it — and only into fields the vet has not filled or touched.
 * Hook-free, so the form's tests reach it without the organization context.
 */
import { format } from 'date-fns';
import { translateKey } from 'src/locales/translate';
import { MedicationDosingSuggestionDto } from 'woofwoof-api';
import { mapDrugForm, parseDrugStrength } from './medicationDrugData';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  MedicineSearchOption,
  medicineOptionName,
} from './medicineSearchOptions';

/** Everything the autofill may fill: never `dosagePending`, never `notes`. */
export const DOSAGE_AUTOFILL_FIELDS = [
  'strengthValue',
  'strengthUnit',
  'strengthUnitCustom',
  'form',
  'formCustom',
  'quantityValue',
  'quantityCustom',
  'frequencyType',
  'frequencyValue',
  'frequencyCustom',
  'durationType',
  'durationValue',
  'durationCustom',
  'dosageSchedule',
] as const;

export type DosageAutofillField = (typeof DOSAGE_AUTOFILL_FIELDS)[number];

export const isDosageAutofillField = (
  name?: string,
): name is DosageAutofillField =>
  (DOSAGE_AUTOFILL_FIELDS as readonly string[]).includes(name ?? '');

export type DosagePrefill = Partial<
  Pick<MedicationsFormModalValuesProps, DosageAutofillField>
>;

/** What a pick tells the autofill about the drug. */
export interface DosageAutofillPick {
  /** The registry id, for the clinic's history; absent for an unlinked Product. */
  medicineProductId?: string;
  /** The recorded name, for the history of a drug without a registry id. */
  name: string;
  strength?: string;
  pharmaceuticalForm?: string;
}

export interface AutofillFieldState {
  value: unknown;
  /** The vet has edited the field in this form session, whatever it holds now. */
  edited: boolean;
}

/** Where the prefilled dose came from, for the notice above the fields. */
export type DosageAutofillNotice =
  | { kind: 'DRUG' }
  | {
      kind: 'HISTORY';
      prescribedAt: string;
      petCategory?: string;
      sameSpecies: boolean;
    };

/**
 * A field and the fields that only mean something beside it land together
 * or not at all; the first one leads (without it the group says nothing).
 */
const AUTOFILL_GROUPS: DosageAutofillField[][] = [
  ['strengthValue', 'strengthUnit', 'strengthUnitCustom'],
  ['form', 'formCustom'],
  ['quantityValue', 'quantityCustom'],
  ['frequencyType', 'frequencyValue', 'frequencyCustom'],
  ['durationType', 'durationValue', 'durationCustom'],
  ['dosageSchedule'],
];

const SPECIES_KEYS: Record<string, string> = {
  dog: 'visit.petCategoryDog',
  cat: 'visit.petCategoryCat',
  other: 'visit.petCategoryOther',
};

const isBlank = (value: unknown): boolean =>
  value === '' || value === undefined || value === null;

const text = (value?: string): string => value?.trim() ?? '';

const numberOrEmpty = (value?: number): number | '' =>
  typeof value === 'number' ? value : '';

function copyField<K extends DosageAutofillField>(
  target: DosagePrefill,
  source: DosagePrefill,
  field: K,
): void {
  target[field] = source[field];
}

/**
 * A registry pick reads the registry; a Warehouse pick its linked registry
 * medicine, the Product's own snapshot filling what the registry lacks (and
 * all of it for an unlinked Product).
 */
export function autofillPickFrom(
  option: MedicineSearchOption,
): DosageAutofillPick {
  if (option.source === 'REGISTRY') {
    const { medicine } = option;
    return {
      medicineProductId: medicine.productId,
      name: medicine.productName,
      strength: medicine.strength,
      pharmaceuticalForm: medicine.pharmaceuticalForm,
    };
  }
  const { medicine, product } = option;
  return {
    medicineProductId: medicine?.productId,
    name: medicineOptionName(option),
    strength: medicine?.strength || product.strength,
    pharmaceuticalForm: medicine?.pharmaceuticalForm || product.form,
  };
}

/** The strength and form the drug itself declares, as form values. */
export function drugDataPrefill(pick: DosageAutofillPick): DosagePrefill {
  return {
    ...(parseDrugStrength(pick.strength) ?? {}),
    ...(mapDrugForm(pick.pharmaceuticalForm) ?? {}),
  };
}

/** How the clinic last prescribed the drug, as form values; blank where it says nothing. */
export function historyPrefill(
  suggestion: MedicationDosingSuggestionDto,
): DosagePrefill {
  return {
    strengthValue: numberOrEmpty(suggestion.strengthValue),
    strengthUnit: suggestion.strengthUnit ?? '',
    strengthUnitCustom: text(suggestion.strengthUnitCustom),
    form: suggestion.form ?? '',
    formCustom: text(suggestion.formCustom),
    quantityValue: numberOrEmpty(suggestion.quantityValue),
    quantityCustom: text(suggestion.quantityCustom),
    frequencyType: suggestion.frequencyType ?? '',
    frequencyValue: numberOrEmpty(suggestion.frequencyValue),
    frequencyCustom: text(suggestion.frequencyCustom),
    durationType: suggestion.durationType ?? '',
    durationValue: numberOrEmpty(suggestion.durationValue),
    durationCustom: text(suggestion.durationCustom),
    dosageSchedule: text(suggestion.dosageSchedule),
  };
}

/**
 * What of `prefill` may land: every group whose lead the prefill carries and
 * whose fields are all empty and untouched — only its non-empty values.
 */
export function autofillUpdates(
  prefill: DosagePrefill,
  stateOf: (field: DosageAutofillField) => AutofillFieldState,
): DosagePrefill {
  const updates: DosagePrefill = {};
  AUTOFILL_GROUPS.forEach((group) => {
    if (isBlank(prefill[group[0]])) return;
    const untouched = group.every((field) => {
      const state = stateOf(field);
      return isBlank(state.value) && !state.edited;
    });
    if (!untouched) return;
    group
      .filter((field) => !isBlank(prefill[field]))
      .forEach((field) => copyField(updates, prefill, field));
  });
  return updates;
}

/** "Podpowiedziano na podstawie podania z 01.09.2026 (pies)", or from the drug's data. */
export function dosageAutofillMessage(notice: DosageAutofillNotice): string {
  if (notice.kind === 'DRUG') {
    return translateKey('medication.form.autofillDrugData');
  }
  const speciesKey = notice.petCategory
    ? SPECIES_KEYS[notice.petCategory]
    : undefined;
  const species =
    notice.sameSpecies && speciesKey
      ? translateKey(speciesKey).toLocaleLowerCase('pl-PL')
      : translateKey('medication.form.autofillOtherSpecies');
  return translateKey('medication.form.autofillHistory', {
    date: format(new Date(notice.prescribedAt), 'dd.MM.yyyy'),
    species,
  });
}
